import { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Save, Upload } from "lucide-react";

export default function CompanyPage() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/company").then(r => setCompany(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/company", { name: company.name, address: company.address, phone: company.phone, email: company.email, website: company.website, rut: company.rut });
      toast.success("Datos guardados");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/company/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo actualizado");
    } catch { toast.error("Error subiendo logo"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (!company) return null;

  return (
    <div data-testid="company-page" className="max-w-3xl">
      <div className="mb-8">
        <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">Mi Empresa</h1>
        <p className="text-slate-500 mt-1">Configura los datos de tu empresa para tus cotizaciones</p>
      </div>

      {/* Logo */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-6">
        <h2 className="font-bold text-lg font-['Satoshi'] mb-4">Logo</h2>
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden" data-testid="company-logo-preview">
            {company.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`} alt="Logo" className="w-full h-full object-contain p-2" />
            ) : (
              <Building2 size={28} className="text-slate-300" />
            )}
          </div>
          <label className="rounded-lg bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 font-medium px-5 py-2.5 text-sm transition-all cursor-pointer flex items-center gap-2">
            <Upload size={16} /> Subir Logo
            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload" />
          </label>
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-6">
        <h2 className="font-bold text-lg font-['Satoshi'] mb-4">Datos de la empresa</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Nombre</label>
            <input type="text" value={company.name || ""} onChange={e => setCompany({ ...company, name: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="company-name" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">RUT</label>
            <input type="text" value={company.rut || ""} onChange={e => setCompany({ ...company, rut: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="company-rut" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Dirección</label>
            <input type="text" value={company.address || ""} onChange={e => setCompany({ ...company, address: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="company-address" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Teléfono</label>
              <input type="text" value={company.phone || ""} onChange={e => setCompany({ ...company, phone: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="company-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Email</label>
              <input type="email" value={company.email || ""} onChange={e => setCompany({ ...company, email: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="company-email" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Sitio web</label>
            <input type="text" value={company.website || ""} onChange={e => setCompany({ ...company, website: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="company-website" />
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="mt-6 rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-5 py-2.5 text-sm transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50"
          data-testid="save-company">
          <Save size={16} /> {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
